import { ColumnDef } from '@tanstack/react-table'
import { CantileverButton } from 'components/CantileverButton'
import { TextCell } from 'components/table/cells/TextCell'
import { ChartTooltip } from 'features/timeSeries/components/ChartTooltip'
import { ColorSwatch } from 'features/timeSeries/components/ColorSwatch'
import { EditableRangeInput } from 'features/timeSeries/components/EditableRangeInput'
import { TruncationTooltip } from 'features/timeSeries/components/TruncationTooltip'
import { getAutoRangePlaceholder } from 'features/timeSeries/components/YScaleControls'
import {
  AxisRange,
  SensorRangeMap,
} from 'features/timeSeries/utils/axisRangeHelpers'
import { X } from 'lucide-react'
import React, { useMemo } from 'react'
import styled from 'styled-components'
import { SensorScatterplotData } from 'types'

const CenteredCell = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
`

const StyledX = styled(X)`
  height: 1rem;
  width: 1rem;
  color: ${({ theme }) => theme.forecast.danger};
`

const NameCellContainer = styled.div`
  display: flex;
  flex-direction: column;
  overflow: hidden;
  cursor: default;

  span {
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
`

const NameSubtitle = styled.span`
  font-size: 0.6875rem;
  color: ${({ theme }) => theme.forecast.secondaryText};
`

const NameCell: React.FC<{
  name: string
  description: string
  isNarrow: boolean
}> = ({ name, description, isNarrow }) => {
  const label =
    isNarrow && description ? (
      <>
        {name}
        <br />
        {description}
      </>
    ) : (
      name
    )

  return (
    <TruncationTooltip
      side='left'
      label={label}
      style={{ minWidth: 0, width: '100%' }}>
      <NameCellContainer>
        <span>{name}</span>
        {description && <NameSubtitle>{description}</NameSubtitle>}
      </NameCellContainer>
    </TruncationTooltip>
  )
}

// Same tooltip surface as NameCell so the Description column matches the rest of
// the menu (the shared TextCell truncation tooltip uses the black TooltipWrapper).
// Truncation-gated: fully visible descriptions get no tooltip, and it opens to
// the left so it never covers the sidebar rows above.
const DescriptionCell: React.FC<{ description?: string }> = ({
  description,
}) =>
  description ? (
    <TruncationTooltip
      side='left'
      label={description}
      style={{ minWidth: 0, width: '100%' }}>
      <NameCellContainer>
        <span>{description}</span>
      </NameCellContainer>
    </TruncationTooltip>
  ) : (
    <TextCell truncate text={description} />
  )

export const SensorScatterplotTableColumns = (
  onRemove: (itemId: string, isDerived?: boolean) => void,
  onRemoveAll: (event: React.MouseEvent<HTMLButtonElement>) => void,
  onYMinChange: (sensorId: string, value: string) => void,
  onYMaxChange: (sensorId: string, value: string) => void,
  sensorRanges: SensorRangeMap,
  onChangeColor: (sensorId: string, color: string) => void,
  isNarrow: boolean,
  /**
   * Single-scale mode: cells stay editable, but a committed edit fans out to
   * every pen (all rows update together — that simultaneous update is the
   * visual cue that the scale is shared). Empty cells then show the SHARED
   * auto range as their placeholder rather than each pen's own data range,
   * since the shared range is what the axis actually uses.
   */
  singleScaleMode: boolean,
  /** The shared auto range (single-scale placeholder); null while no data. */
  sharedAutoRange: AxisRange | null,
  /** Once true, an empty range means "No data"; before that it is unknown. */
  rangesSettled: boolean
): ColumnDef<SensorScatterplotData>[] => {
  return useMemo(() => {
    const columns: ColumnDef<SensorScatterplotData>[] = [
      {
        id: 'remove',
        header: () => (
          <CenteredCell>
            <ChartTooltip label='Remove all pens' side='left'>
              <CantileverButton iconOnly onClick={(e) => onRemoveAll(e)}>
                <StyledX />
              </CantileverButton>
            </ChartTooltip>
          </CenteredCell>
        ),
        cell: ({ row }) => (
          <CenteredCell>
            <ChartTooltip label='Remove pen' side='left'>
              <CantileverButton
                iconOnly
                onClick={() =>
                  onRemove(row.original.id, row.original.isDerived)
                }>
                <StyledX />
              </CantileverButton>
            </ChartTooltip>
          </CenteredCell>
        ),
      },
      {
        accessorKey: 'name',
        id: 'name',
        header: 'Tag Name',
        cell: ({ row }) => (
          <NameCell
            name={row.original.name}
            description={row.original.description}
            isNarrow={isNarrow}
          />
        ),
      },
      {
        accessorKey: 'description',
        id: 'description',
        header: 'Description',
        cell: ({ row }) => (
          <DescriptionCell description={row.original.description} />
        ),
      },
    ]

    columns.push(
      {
        accessorKey: 'yMin',
        id: 'yMin',
        header: 'Y Min',
        cell: ({ row }) => (
          <EditableRangeInput
            value={row.original.yMin}
            placeholder={getAutoRangePlaceholder(
              singleScaleMode
                ? sharedAutoRange?.min
                : sensorRanges[row.original.id]?.min,
              rangesSettled
            )}
            onCommit={(value) => onYMinChange(row.original.id, value)}
            ariaLabel={`${row.original.name} Y min`}
          />
        ),
      },
      {
        accessorKey: 'yMax',
        id: 'yMax',
        header: 'Y Max',
        cell: ({ row }) => (
          <EditableRangeInput
            value={row.original.yMax}
            placeholder={getAutoRangePlaceholder(
              singleScaleMode
                ? sharedAutoRange?.max
                : sensorRanges[row.original.id]?.max,
              rangesSettled
            )}
            onCommit={(value) => onYMaxChange(row.original.id, value)}
            ariaLabel={`${row.original.name} Y max`}
          />
        ),
      }
    )

    columns.push({
      accessorKey: 'color',
      id: 'color',
      header: 'Color',
      cell: ({ row }) => (
        <ColorSwatch
          color={row.original.color}
          onChangeColor={(color) => onChangeColor(row.original.id, color)}
        />
      ),
    })

    return columns
  }, [
    onRemove,
    onRemoveAll,
    onYMinChange,
    onYMaxChange,
    sensorRanges,
    onChangeColor,
    isNarrow,
    singleScaleMode,
    sharedAutoRange,
    rangesSettled,
  ])
}
